<template>
  <div :class="[`mention-items ${editor.id}`, { mobile: isMobile }]">
    <MentionTabs
      :results="items"
      :search-query="items.query"
      :selected-index="selectedIndexInResults"
      :tabs-order="$constants.searchGroupsOrder"
      @tabClick="selectTab"
    />
    <SearchResults
      ref="searchResults"
      :search-result-members="items.members"
      :search-result-cards="items.cards"
      :search-result-boards="items.boards"
      :search-result-pages="items.pages"
      :search-result-projects="items.projects"
      :search-result-epics="items.epics"
      :search-query="items.query"
      :groups-order="$constants.searchGroupsOrder"
      :is-grouped="true"
      :tab-select="true"
      :manually-displayed="true"
      :show-empty-groups-state="true"
      :type="$constants.searchResultsType.MentionList"
      @selectedIndexChange="indexChangeHandler"
      @selectSearchResult="selectResult"
    />
    <div
      v-if="noResults"
      class="item"
    >
      {{ translate('noResults') }}
    </div>
  </div>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import generateLink from '@/utilities/generateLink';
import MentionTabs from './MentionTabs.vue';

const SearchResults = defineAsyncComponent(
  () => import(/* webpackChunkName: "searchResults" */ '@/components/elements/SearchResults.vue')
);

export default {
  components: {
    SearchResults,
    MentionTabs,
  },

  props: {
    items: {
      type: Object,
      required: true,
    },

    command: {
      type: Function,
      required: false,
      default: () => {},
    },

    editor: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      selectedIndexInResults: 0,
    };
  },

  computed: {
    noResults() {
      return (
        !this.items.members.length &&
        !this.items.cards.length &&
        !this.items.epics.length &&
        !this.items.boards.length &&
        !this.items.pages.length &&
        !this.items.projects.length
      );
    },
  },

  mounted() {
    this.editor.commands.setMentionDropdownActive(true);
  },

  unmounted() {
    this.editor.commands.setMentionDropdownActive(false);
  },

  methods: {
    selectTab(index, tab) {
      if (this.$refs.searchResults.suspendMouseEnter) {
        this.$refs.searchResults.mouseMoveHandle();
      }
      this.$refs.searchResults.scrollToGroup(tab);
      this.$nextTick(() => {
        this.$refs.searchResults.setSelectedIndex(index, true);
      });
    },
    indexChangeHandler(index) {
      this.selectedIndexInResults = index;
    },
    onKeyDown({ event }) {
      switch (event.key) {
        case 'ArrowUp':
        case 'ArrowDown':
        case 'Enter':
        case 'Tab':
          return true;
        default:
          break;
      }
      return false;
    },

    selectResult(result, type) {
      switch (type) {
        case 'member':
          this.insertMember(result);
          break;
        case 'card':
        case 'board':
        case 'page':
        case 'space':
          this.insertLink(result, type);
          break;
        case 'epic':
        case 'project':
          this.insertLink(result, 'project');
          break;
        default:
          break;
      }
    },

    insertLink(resource, type) {
      const teamSlug = this.$store.getters.getTeamSlug;
      this.command({
        'data-url': generateLink(type, teamSlug, resource.id, resource.title),
        name: resource.title,
        nodeName: 'internal-link',
        'data-get-resource': true,
      });
    },

    insertMember(member) {
      this.command({
        id: member.id,
        label: member.display_name,
        userId: member.id,
        denotationChar: '@',
        mentionTime: `${Math.floor(new Date().getTime() / 1000)}`,
        userValue: member.value,
        nodeName: 'mention',
      });
    },
  },
};
</script>

<style lang="scss">
.mention-items {
  position: relative;
  border: 1px solid;
  box-shadow: 0 4px 8px rgb(0 0 0 / 16%);
  border-radius: 8px;
  padding-left: 0;
  padding-right: 0;
  z-index: 1;
  width: 400px;
  max-height: 584px;
  overflow: hidden;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  @apply bg-important-bg border-important-border text-text-default;

  .search-results__groups {
    border-top: none;
  }

  &.mobile {
    width: 352px;
  }
}

.item {
  display: block;
  margin: 0;
  width: 100%;
  text-align: left;
  background: transparent;
  border-radius: 0.4rem;
  border: 1px solid transparent;
  padding: 0.375rem 0.5rem;

  &.is-selected {
    border-color: #000;
  }
}
</style>
